import { Injectable, QueryList, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AttributeBase, Leg } from '@approot/models';
import { ConfigService, ProductsConfigService } from '@approot/services';
import { getOppositePosition, Utilities } from '@approot/utilities';
import { getScheduleAttributes } from '@approot/utilities/schedule-utilities';
import { DynamicLegAttributeComponent } from '../../dynamic-leg-attribute/dynamic-leg-attribute.component';
import { getAttributeControlFromKey } from '../utilities/leg-attribute-utilities';
import {
  blurRounding,
  fieldNumber,
  makeAttributeUseCurrentValue,
  makeTextCustomColor,
  scheduleLegAttributeChange,
  setButtonText
} from '../utilities/leg.utilities';
import { onTotalNotionalBlur } from '../utilities/notional-utilities';
import { LegFwdVolService } from './leg-fwd-vol.service';
import { LegHelperService } from './leg-helper.service';
import { LegNotionalService } from './leg-notional.service';
import { LegPricingService } from './leg-pricing.service';
import { LegScheduleService } from './leg-schedule.service';
import {
  getStrikeValue,
  LegSpotStrikeService
} from './leg-spot-strike.service';

@Injectable({
  providedIn: 'root'
})
export class LegElemService {
  constructor(
    private legHelperService: LegHelperService,
    private renderer: Renderer2,
    private legSpotStrikeService: LegSpotStrikeService,
    private legFwdVolService: LegFwdVolService,
    private legPricingService: LegPricingService,
    private productsConfigService: ProductsConfigService,
    private legScheduleService: LegScheduleService,
    private legNotionalService: LegNotionalService,
    private configService: ConfigService
  ) {}

  public inputBlur(input: FocusEvent): void {
    const leg = this.legHelperService.leg;
    const fieldName = input.target['id'];
    if (
      getAttributeControlFromKey(fieldName, leg.attributesAsFormControls)
        .readOnly
    ) {
      return;
    }
    this.legHelperService.onFieldBlurredFn(fieldName);
  }

  public inputClick(input: FocusEvent): void {
    // highlights all text of an input box on focus
    if (input.target) {
      this.renderer.selectRootElement(input.target).select();
    }
  }

  public allInputElementsListener(allInputs: Element | Element[]): void {
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    let elements: Element[];
    if (allInputs instanceof Element) {
      elements = [allInputs];
    } else {
      elements = allInputs;
    }
    elements.forEach(element => {
      element.addEventListener('focus', this.inputClick.bind(this));
      if (rfqExecutionSettings.rfqLeg) {
        element.addEventListener('blur', this.inputBlur.bind(this));
      }
    });
  }

  public initListeners(
    legComponents: QueryList<DynamicLegAttributeComponent>
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    this.allElemListener();
    this.scheduleElemListener();
    this.basketElemListener(leg, form);
    this.optionsPortfolioListener(leg, form);
    this.optionsPortfolioSpotsListener(leg, form);
    this.strikeListener();
    this.targetProfitListener();
    this.volListener();
    this.forwardPointListener(leg, form);
    this.rateListener(leg, legComponents);
    this.spotListener();
    this.scheduleAmountListener();
    this.notionalListener();
  }

  public onScheduleClick(elem: Event): void {
    const leg = this.legHelperService.leg;
    const scheduleName = elem.target['id'];
    leg.scheduleColumnNames = getScheduleAttributes(
      scheduleName,
      leg,
      this.configService.userSettings()
    );
    leg.scheduleOpened = scheduleName;
  }

  public setBasketModalState(modalState: boolean): void {
    const leg = this.legHelperService.leg;
    leg.showBasket = !!modalState;
  }

  public setOptionsPortfolioModalState(modalState: boolean): void {
    const leg = this.legHelperService.leg;
    leg.showOptionsPortfolio = modalState;
  }

  public setOptionsPortfolioSpotsModalState(modalState: boolean): void {
    const leg = this.legHelperService.leg;
    leg.showOptionsPortfolioSpots = modalState;
  }

  public strikesEventsListener(elem: Element): void {
    elem.addEventListener('focus', this.onStrikeFocus.bind(this));
    elem.addEventListener('blur', this.onStrikeBlur.bind(this));
    elem.addEventListener('input', this.onStrikeInput.bind(this));
  }

  public allElemListener(specificElem?: string): void {
    let allInputs = this.legHelperService.qsa(
      specificElem
        ? `.form-row__leg-attributes input#${specificElem}`
        : '.form-row__leg-attributes input'
    );

    if (allInputs) {
      this.allInputElementsListener(allInputs);
    }
  }

  private onStrikeFocus(elem: Event): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    leg.previousValuesOnFocus[elem.target['id']] = form.get(
      elem.target['id']
    ).value;
  }

  private onStrikeBlur(elem: Event): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;

    const field = elem.target['id'];
    // when a strike is blured and its value is a number we want calculate the notional Currency and get the latests vols
    const strikeNumber = fieldNumber(field);

    // if the value is not a number then make it a number

    const strikeValue = getStrikeValue(form.get(field));
    if (leg.previousValuesOnFocus[field] !== strikeValue) {
      this.legSpotStrikeService.setNotionalOnStrikeChange(strikeNumber);
      if (!isNaN(strikeValue)) {
        this.legFwdVolService.calcImpliedVolatility(strikeNumber); // try to get the latest vols
      }
    }
    leg.previousValuesOnFocus[field] = '';
  }

  private onStrikeInput(elem: Event): void {
    const leg = this.legHelperService.leg;
    const strikeField = elem.target['id'];
    leg.shortcutValueUsed[strikeField] = null;
    makeAttributeUseCurrentValue(leg, strikeField, false);
  }

  private scheduleElemListener(): void {
    [
      'schedule',
      'fixedLegSchedule',
      'fixedLegSchedule1',
      'fixedLegSchedule2',
      'floatingLegSchedule',
      'exerciseSchedule'
    ].forEach(schedule => {
      let field = this.legHelperService.qs(`button#${schedule}`);
      if (field) {
        field.addEventListener('click', this.onScheduleClick.bind(this));
      }
    });
  }

  private basketElemListener(leg: Leg, form: FormGroup): void {
    let basket = this.legHelperService.qs('button#basket');
    if (basket) {
      if (
        !leg.preSetPosition ||
        !leg.preSetPosition['basket'] ||
        !leg.preSetPosition['basket'].length
      ) {
        setButtonText('basket', 'Currency Pairs', leg, form);
      }
      basket.addEventListener('click', this.setBasketModalState.bind(this));
    }
  }

  private optionsPortfolioListener(leg: Leg, form: FormGroup): void {
    let optionsPortfolio = this.legHelperService.qs('button#optionsPortfolio');
    if (optionsPortfolio) {
      if (
        !leg.preSetPosition ||
        !leg.preSetPosition['optionsPortfolio'] ||
        !leg.preSetPosition['optionsPortfolio'].length
      ) {
        // we want by default optionsPortfolio to say 0 options if there are none
        setButtonText('optionsPortfolio', 'Options', leg, form);
      }

      optionsPortfolio.addEventListener(
        'click',
        this.setOptionsPortfolioModalState.bind(this)
      );
    }
  }

  private optionsPortfolioSpotsListener(leg: Leg, form: FormGroup): void {
    let optionsPortfolioSpots = this.legHelperService.qs(
      'button#optionsPortfolioSpots'
    );
    if (optionsPortfolioSpots) {
      if (!leg?.preSetPosition['optionsPortfolioSpots']?.length) {
        // we want by default optionsPortfolioSpts to say 0 Options Spots if there are none
        setButtonText('optionsPortfolioSpots', 'Options Spots', leg, form);
      }
      optionsPortfolioSpots.addEventListener(
        'click',
        this.setOptionsPortfolioSpotsModalState.bind(this)
      );
    }
  }

  private strikeListener(): void {
    ['strike1', 'strike2', 'strike'].forEach(strike => {
      const field = this.legHelperService.qs(`#${strike}`);
      if (field) {
        this.strikesEventsListener(field);
      }
    });
  }

  private targetProfitListener(): void {
    let targetProfit = this.legHelperService.qs('#targetProfit');
    if (targetProfit) {
      targetProfit.addEventListener('blur', this.onTargetProfitBlur.bind(this));
    }
  }

  private onTargetProfitBlur(): void {
    // Apply changes to TP when user clicks out
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    let targetProfitCtrl = form.get('targetProfit');
    let tPUnit = form.get('targetProfitUnit');
    let targetProfitVal = getAttributeControlFromKey(
      'targetProfit',
      leg.attributesAsFormControls
    ).validator;

    if (!(targetProfitCtrl && tPUnit)) {
      return;
    }

    if (tPUnit.value === 'Spot') {
      const tp = (targetProfitCtrl.value as string)?.replace(/,/g, '').trim();
      let val: string;
      if (tp == null || tp === '' || isNaN(parseFloat(tp))) {
        val = '';
      } else {
        val = parseFloat(tp).toFixed(5);
      }
      targetProfitCtrl.setValue(val, { emitEvent: false });
    }

    if (
      tPUnit.value !== 'None' &&
      tPUnit.value !== 'Common Currency' &&
      tPUnit.value !== 'Spot'
    ) {
      if (targetProfitCtrl) {
        targetProfitCtrl.setValue(targetProfitCtrl.value + ' ' + tPUnit.value);
      }
    }

    if (tPUnit.value === 'Common Currency') {
      if (
        // !targetProfitVal.includes('positiveInteger') &&
        !targetProfitVal.includes('numericShorthand') &&
        !targetProfitVal.includes('numericDisplay')
      ) {
        // Check if the field does not contains the validator for "positive integer"
        // targetProfitVal.push('positiveInteger'); // Set the validator to this field if true
        targetProfitVal.push('numericShorthand'); // Add numeric shorthand
        targetProfitVal.push('numericDisplay');
      }
    }
    targetProfitCtrl.updateValueAndValidity(); // Cover for potential change of TP Unit from something else back to 'Spot'
  }

  private volListener(): void {
    ['volAsk', 'volBid', 'vol1Bid', 'vol2Bid', 'vol1Ask', 'vol2Ask'].forEach(
      vol => {
        const field = this.legHelperService.qs(`#${vol}`);
        if (field) {
          this.volsListener(field);
        }
      }
    );
  }

  private volsListener(elem: Element): void {
    const leg = this.legHelperService.leg;
    elem.addEventListener('blur', this.volsBlur.bind(this));
    elem.addEventListener('input', (event: Event) =>
      makeAttributeUseCurrentValue(leg, event.target['id'])
    );
  }

  private volsBlur(elem: Event): void {
    // when vol is blured and its empty get the latest vols
    const form = this.legHelperService.form;
    const fieldName = elem.target['id'];
    const volNumber =
      fieldName === 'volBid' || fieldName === 'volAsk'
        ? ''
        : fieldName === 'vol1Bid' || fieldName === 'vol1Ask'
          ? '1'
          : '2';
    const ctrl = form.get(fieldName);
    if (ctrl.value.trim() === '') {
      ctrl.setValue('');
      this.legFwdVolService.calcImpliedVolatility(volNumber, fieldName);
    }
  }

  private forwardPointListener(leg: Leg, form: FormGroup): void {
    ['#forwardPointsAsk', '#forwardPointsBid'].forEach(key => {
      let forwardPoints = this.legHelperService.qs(key);
      if (forwardPoints) {
        this.forwardPointElemListener(forwardPoints, leg, form);
      }
    });
  }

  private forwardPointElemListener(
    elem: Element,
    leg: Leg,
    form: FormGroup
  ): void {
    elem.addEventListener('blur', (event: any) => {
      const ctrl = form.get(event.target.id);
      if (ctrl?.value.trim() === '') {
        ctrl.setValue('');
        this.legFwdVolService.calcForwardPoint(event.target.id);
      }
    });
    elem.addEventListener('input', (event: Event) =>
      makeAttributeUseCurrentValue(leg, event.target['id'])
    );
  }

  private rateListener(
    leg: Leg,
    legComponents: QueryList<DynamicLegAttributeComponent>
  ): void {
    setTimeout(() => {
      // Preformat - must be a less fragile way to do this
      this.formatFieldOnMarginStyle('retrocessionFee');
      this.formatFieldOnMarginStyle('margin');
    }, 2000);
    [
      'conversionRate',
      'margin',
      'marginBid',
      'marginAsk',
      'depositRate',
      'retrocessionFee'
    ].forEach(fieldName => {
      const fieldElement = this.legHelperService.qs(`#${fieldName}`);
      if (fieldElement) {
        fieldElement.addEventListener(
          // if they click on an un-editable area
          'blur',
          () => {
            if (
              fieldName === 'margin' ||
              fieldName === 'marginBid' ||
              fieldName === 'marginAsk' ||
              fieldName === 'retrocessionFee'
            ) {
              this.formatFieldOnMarginStyle(fieldName);
            }

            if (leg.productType === 'newdcd') {
              this.legPricingService.repriceDcdChange(fieldName, legComponents);
            }
          }
        );
      }
    });
  }

  private spotListener(): void {
    ['spot', 'spotBid', 'spotAsk', 'secondSpot'].forEach(spot => {
      const field = this.legHelperService.qs(`#${spot}`);
      if (field) {
        this.spotEventListener(field);
      }
    });
  }

  public spotEventListener(elem: Element): void {
    elem.addEventListener('blur', this.spotHandlerPrep.bind(this));
    elem.addEventListener('input', this.spotManualChange.bind(this));
  }

  private spotManualChange(event: Event): void {
    // if the spot changes manually then make the text orange, and set the variables to no longer update the spot
    const leg = this.legHelperService.leg;
    if (event) {
      const target = event.target as Element;
      leg.manuallyEnteredSpot = true;

      if (target.id === 'spot') {
        // if the spot is manually changed we are no longer using the latest spot anymore
        leg.usingLatestValues.spot = false;
        leg.usingLatestValues.spotDate = false;
      } else if (target.id === 'secondSpot') {
        leg.usingLatestValues.secondSpot = false;
      }
      makeTextCustomColor(target.id, Utilities.orangeTextColor, leg);
    }
  }

  private spotHandlerPrep(event: Event): void {
    // when the spot is blured and empty the newest spot is fetched
    const form = this.legHelperService.form;
    const fieldId = event.target['id'];
    const crossOrSecondCross =
      fieldId === 'spot' || fieldId === 'spotBid' || fieldId === 'spotAsk'
        ? 'cross'
        : 'secondCross'; // we need to figure out if we need the cross if its a spot
    // or the secondCross if its the secondSpot

    let cross = '';

    if (form.get(crossOrSecondCross)) {
      cross = form.get(crossOrSecondCross).value;
    } else if (
      form.get('alternativeCurrency')?.value &&
      form.get('depositCurrency')?.value
    ) {
      // if no cross/second cross
      cross = this.productsConfigService.getCrossFromCurrencies(
        form.get('depositCurrency').value,
        form.get('alternativeCurrency').value
      );
    }

    if (
      cross &&
      form.get(event.target['id']) &&
      !form.get(event.target['id']).value
    ) {
      this.legSpotStrikeService.spotCaller(cross, event.target['id'], true);
    }
  }

  private scheduleAmountListener(): void {
    ['nbFixings', 'nbExpiries', 'nbSettlementDates', 'nbExerciseDates'].forEach(
      numberOf => {
        const numberField = this.legHelperService.qs(`#${numberOf}`);
        if (numberField) {
          this.scheduleNumberAmountListener(numberField);
        }
      }
    );
  }

  private scheduleNumberAmountListener(elem: Element): void {
    let focusValues = { value: '' }; // focus data is stored here
    elem.addEventListener('focus', function (focusElem) {
      focusValues.value = focusElem.target['value']; // store the current value on focus
    });
    elem.addEventListener(
      'blur',
      this.scheduleAmountOnBlur.bind(this, focusValues)
    );
  }

  private scheduleAmountOnBlur(focusValues: { value: any }, elem: Event): void {
    // when the number of fixings/settlementDates/expiries is blured
    // generate a new schedule.
    if (focusValues.value !== elem.target['value']) {
      // if the current value has changed from the previous value
      if (elem.target['id'] === 'nbExerciseDates') {
        this.legScheduleService.exerciseScheduleGenerator();
      } else {
        this.legScheduleService.callScheduleGenerator();
      }
    }
  }

  private notionalListener(): void {
    [
      'notional',
      'counterNotional',
      'notional1',
      'counterNotional1',
      'notional2',
      'counterNotional2'
    ].forEach(notional => {
      const field = this.legHelperService.qs(`#${notional}`);
      if (field) {
        field.addEventListener('input', this.recordFieldToBeEdited.bind(this));
      }
    });

    const totalNotionalElem = this.legHelperService.qs(`#totalNotional`);
    if (totalNotionalElem) {
      totalNotionalElem.addEventListener('blur', _ =>
        onTotalNotionalBlur(
          this.legHelperService.form,
          this.legHelperService.leg
        )
      );
    }
  }

  private recordFieldToBeEdited(field: Event): void {
    const form = this.legHelperService.form;
    switch (field.target['id']) {
      case 'notional':
      case 'counterNotional':
        if (form.get('notional') && form.get('counterNotional'))
          this.legNotionalService.handleNotionalSelectedRecords(
            '',
            field.target['id'],
            true
          );
        break;
      case 'notional1':
      case 'counterNotional1':
        if (form.get('notional1') && form.get('counterNotional1'))
          this.legNotionalService.handleNotionalSelectedRecords(
            '1',
            field.target['id'],
            true
          );
        break;
      case 'notional2':
      case 'counterNotional2':
        if (form.get('notional2') && form.get('counterNotional2'))
          this.legNotionalService.handleNotionalSelectedRecords(
            '2',
            field.target['id'],
            true
          );
        break;
    }
  }

  public handleNumberField(attribute: AttributeBase<any>, init = false): void {
    if (init && attribute.format === 'percentage') {
      this.percentageFormCtrlListener(attribute.key);
    }
    this.numberElementListener(attribute);
  }

  private numberElementListener(attribute: AttributeBase<any>): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const isPercentage = attribute.format === 'percentage';
    if (isPercentage || attribute.decimalPlaces != null) {
      const fieldElement = this.legHelperService.qs(`#${attribute.key}`);
      fieldElement?.addEventListener('blur', _ => {
        blurRounding(attribute.key, form, leg);
      });
    }
  }

  private percentageFormCtrlListener(attributeName: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const ctrl = form.get(attributeName);
    this.legHelperService.subSink.sink = ctrl.valueChanges.subscribe(val => {
      const correspondingPosition = getOppositePosition(attributeName);
      if (!correspondingPosition) {
        const percentageOrNot = val ? '%' : '';
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + attributeName),
          form.get(attributeName),
          percentageOrNot,
          !!percentageOrNot,
          'percentage',
          this.renderer
        );
      }

      switch (attributeName) {
        case 'fixedLegRate':
          scheduleLegAttributeChange(
            form,
            leg,
            attributeName,
            'fixedLegSchedule'
          );
          break;
        case 'fixedLegRate1':
        case 'fixedLegRate2':
          scheduleLegAttributeChange(
            form,
            leg,
            attributeName,
            attributeName === 'fixedLegRate1'
              ? 'fixedLegSchedule1'
              : 'fixedLegSchedule2',
            'fixedLegRate'
          );
          break;
        default:
          break;
      }
    });
  }

  public setToTradesElementsAfter(): void {
    // listen to trade elements
    // this should only happen once, as we do not want duplicate listeners
    const leg = this.legHelperService.leg;
    setTimeout(() => {
      const allTradesInput = leg.attributesAsFormControls
        .map(attribute => {
          if (attribute.attributeType === 'tradeAttribute') {
            let input = this.legHelperService.qs(
              '.form-row__leg-attributes input#' + attribute.key
            );
            const ctrl = this.legHelperService.form.get(attribute.key);
            ctrl.markAsTouched();
            ctrl.updateValueAndValidity();
            if (input) {
              return input;
            }
          }
          return null;
        })
        .filter(inputElem => inputElem);
      this.allInputElementsListener(allTradesInput);
    });
  }

  private formatFieldOnMarginStyle(
    field: 'margin' | 'marginAsk' | 'marginBid' | 'retrocessionFee'
  ): void {
    const form = this.legHelperService.form;
    const ctrl = form?.get(field);
    const marginStyle = form?.get('marginStyle')?.value;
    if (!(ctrl && marginStyle)) {
      return;
    }
    const shouldRound =
      marginStyle === 'Pips' ||
      marginStyle === 'Percent Term' ||
      marginStyle === 'Percent Base' ||
      marginStyle === 'Vol Spread';

    if (shouldRound) {
      ctrl.setValue(Utilities.roundNumberToDp(parseFloat(ctrl.value), 3));
    }
  }
}
